import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  styled,
} from "@mui/material";
import CInput from "../../../../components/CInput";
import {
  createScope,
  updateScope,
  getProjectRoomBoqs,
  getScopeDetails,
  getScopeBoqs,
} from "../../../../services/scopeService";

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
  "&:hover": {
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
  },
  "&:disabled": {
    opacity: 0.7,
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
    cursor: "not-allowed",
  },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const AddScopeModal = ({ open, onClose, onSave, editingScope, projectId }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [roomBoqsData, setRoomBoqsData] = useState(null);
  const [scopeDetailsData, setScopeDetailsData] = useState(null);

  const [selectedItems, setSelectedItems] = useState({
    rooms: new Set(),
    inclusions: new Set(),
    boqs: new Set(),
  });
  const [expandedRooms, setExpandedRooms] = useState(new Set());
  const [expandedInclusions, setExpandedInclusions] = useState(new Set());

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, projectId, editingScope]);

  const loadData = async () => {
    try {
      setLoadingData(true);

      const [roomBoqsResponse, scopeDetailsResponse] = await Promise.all([
        editingScope
          ? getScopeBoqs(projectId, editingScope.scope_id)
          : getScopeBoqs(projectId),
        editingScope
          ? getScopeDetails(editingScope.scope_id)
          : Promise.resolve({ success: true, rooms: [] }),
      ]);

      if (roomBoqsResponse.success) {
        setRoomBoqsData(roomBoqsResponse);
      }

      if (scopeDetailsResponse.success) {
        setScopeDetailsData(scopeDetailsResponse);
      }

      // Initialize form data
      if (editingScope) {
        setFormData({
          name: editingScope.name || "",
          description: editingScope.description || "",
        });

        // Pre-select items from scope details
        if (scopeDetailsResponse?.rooms && roomBoqsResponse?.rooms) {
          const selectedRooms = new Set();
          const selectedInclusions = new Set();
          const selectedBoqs = new Set();

          scopeDetailsResponse.rooms.forEach((scopeRoom) => {
            const fullRoom = roomBoqsResponse.rooms.find(
              (r) => r.room_id === scopeRoom.room_id
            );
            if (!fullRoom) return;

            const allInclusionsSelected = fullRoom.inclusions?.every(
              (fullInclusion) => {
                const scopeInclusion = scopeRoom.inclusions?.find(
                  (si) => si.id === fullInclusion.id
                );
                if (!scopeInclusion) return false;

                return fullInclusion.boqs?.every((fullBoq) =>
                  scopeInclusion.boqs?.some((sb) => sb.id === fullBoq.id)
                );
              }
            );

            if (allInclusionsSelected) {
              selectedRooms.add(scopeRoom.room_id);
              scopeRoom.inclusions?.forEach((scopeInclusion) => {
                selectedInclusions.add(scopeInclusion.id);
                scopeInclusion.boqs?.forEach((scopeBoq) => {
                  selectedBoqs.add(scopeBoq.id);
                });
              });
            } else {
              scopeRoom.inclusions?.forEach((scopeInclusion) => {
                const fullInclusion = fullRoom.inclusions?.find(
                  (fi) => fi.id === scopeInclusion.id
                );
                if (!fullInclusion) return;

                const allBoqsSelected = fullInclusion.boqs?.every((fullBoq) =>
                  scopeInclusion.boqs?.some((sb) => sb.id === fullBoq.id)
                );

                if (allBoqsSelected) {
                  selectedInclusions.add(scopeInclusion.id);
                  scopeInclusion.boqs?.forEach((scopeBoq) => {
                    selectedBoqs.add(scopeBoq.id);
                  });
                } else {
                  scopeInclusion.boqs?.forEach((scopeBoq) => {
                    selectedBoqs.add(scopeBoq.id);
                  });
                }
              });
            }
          });

          setSelectedItems({
            rooms: selectedRooms,
            inclusions: selectedInclusions,
            boqs: selectedBoqs,
          });
        }
      } else {
        setFormData({ name: "", description: "" });
        setSelectedItems({
          rooms: new Set(),
          inclusions: new Set(),
          boqs: new Set(),
        });
      }

      // Set all rooms and inclusions as expanded by default
      const allRoomIds = new Set(
        roomBoqsResponse?.rooms?.map((room) => room.room_id) || []
      );
      const allInclusionIds = new Set();
      roomBoqsResponse?.rooms?.forEach((room) => {
        room.inclusions?.forEach((inclusion) => {
          allInclusionIds.add(inclusion.id);
        });
      });

      setExpandedRooms(allRoomIds);
      setExpandedInclusions(allInclusionIds);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const toggleRoomExpansion = (roomId) => {
    setExpandedRooms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(roomId)) {
        newSet.delete(roomId);
      } else {
        newSet.add(roomId);
      }
      return newSet;
    });
  };

  const toggleInclusionExpansion = (inclusionId) => {
    setExpandedInclusions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(inclusionId)) {
        newSet.delete(inclusionId);
      } else {
        newSet.add(inclusionId);
      }
      return newSet;
    });
  };

  const handleRoomToggle = (roomId) => {
    const room = roomBoqsData?.rooms?.find((r) => r.room_id === roomId);
    if (!room) return;

    setSelectedItems((prev) => {
      const newSelected = { ...prev };

      if (newSelected.rooms.has(roomId)) {
        newSelected.rooms.delete(roomId);
        room.inclusions?.forEach((inclusion) => {
          newSelected.inclusions.delete(inclusion.id);
          inclusion.boqs?.forEach((boq) => {
            newSelected.boqs.delete(boq.id);
          });
        });
      } else {
        newSelected.rooms.add(roomId);
        room.inclusions?.forEach((inclusion) => {
          newSelected.inclusions.add(inclusion.id);
          inclusion.boqs?.forEach((boq) => {
            newSelected.boqs.add(boq.id);
          });
        });
      }

      return newSelected;
    });
  };

  const handleInclusionToggle = (inclusionId, roomId) => {
    const room = roomBoqsData?.rooms?.find((r) => r.room_id === roomId);
    const inclusion = room?.inclusions?.find((i) => i.id === inclusionId);
    if (!inclusion) return;

    setSelectedItems((prev) => {
      const newSelected = { ...prev };

      if (newSelected.inclusions.has(inclusionId)) {
        newSelected.inclusions.delete(inclusionId);
        inclusion.boqs?.forEach((boq) => {
          newSelected.boqs.delete(boq.id);
        });
      } else {
        newSelected.inclusions.add(inclusionId);
        inclusion.boqs?.forEach((boq) => {
          newSelected.boqs.add(boq.id);
        });
      }

      const allInclusionsSelected = room.inclusions?.every((inc) =>
        newSelected.inclusions.has(inc.id)
      );

      if (allInclusionsSelected) {
        newSelected.rooms.add(roomId);
      } else {
        newSelected.rooms.delete(roomId);
      }

      return newSelected;
    });
  };

  const handleBoqToggle = (boqId, inclusionId, roomId) => {
    const room = roomBoqsData?.rooms?.find((r) => r.room_id === roomId);
    const inclusion = room?.inclusions?.find((i) => i.id === inclusionId);
    if (!inclusion) return;

    setSelectedItems((prev) => {
      const newSelected = { ...prev };

      if (newSelected.boqs.has(boqId)) {
        newSelected.boqs.delete(boqId);
      } else {
        newSelected.boqs.add(boqId);
      }

      const allBoqsSelected = inclusion.boqs?.every((boq) =>
        newSelected.boqs.has(boq.id)
      );

      if (allBoqsSelected) {
        newSelected.inclusions.add(inclusionId);
      } else {
        newSelected.inclusions.delete(inclusionId);
      }

      const allInclusionsSelected = room.inclusions?.every((inc) =>
        newSelected.inclusions.has(inc.id)
      );

      if (allInclusionsSelected) {
        newSelected.rooms.add(roomId);
      } else {
        newSelected.rooms.delete(roomId);
      }

      return newSelected;
    });
  };

  const buildReferencesPayload = () => {
    const references = [];

    if (selectedItems.rooms.size > 0) {
      references.push({
        refType: "ROOM",
        Ids: Array.from(selectedItems.rooms),
      });
    }

    const inclusionsNotCoveredByRooms = new Set();
    selectedItems.inclusions.forEach((inclusionId) => {
      const room = roomBoqsData?.rooms?.find((r) =>
        r.inclusions?.some((inc) => inc.id === inclusionId)
      );
      if (room && !selectedItems.rooms.has(room.room_id)) {
        inclusionsNotCoveredByRooms.add(inclusionId);
      }
    });

    if (inclusionsNotCoveredByRooms.size > 0) {
      references.push({
        refType: "INCLUSION",
        Ids: Array.from(inclusionsNotCoveredByRooms),
      });
    }

    const boqsNotCoveredByRoomsOrInclusions = new Set();
    selectedItems.boqs.forEach((boqId) => {
      const room = roomBoqsData?.rooms?.find((r) =>
        r.inclusions?.some((inc) => inc.boqs?.some((boq) => boq.id === boqId))
      );
      const inclusion = room?.inclusions?.find((inc) =>
        inc.boqs?.some((boq) => boq.id === boqId)
      );

      if (
        room &&
        !selectedItems.rooms.has(room.room_id) &&
        inclusion &&
        !selectedItems.inclusions.has(inclusion.id)
      ) {
        boqsNotCoveredByRoomsOrInclusions.add(boqId);
      }
    });

    if (boqsNotCoveredByRoomsOrInclusions.size > 0) {
      references.push({
        refType: "BOQ",
        Ids: Array.from(boqsNotCoveredByRoomsOrInclusions),
      });
    }

    return references;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Scope name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length > 1000) {
      newErrors.description = "Description cannot exceed 1000 characters";
    }

    const references = buildReferencesPayload();
    if (references.length === 0) {
      newErrors.items = "Please select at least one item";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      const references = buildReferencesPayload();

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        references,
      };

      if (editingScope) {
        await updateScope(editingScope.scope_id, payload);
        toast.success("Scope updated successfully!");
      } else {
        await createScope(projectId, payload);
        toast.success("Scope created successfully!");
      }

      onSave();
    } catch (error) {
      console.error("Error saving scope:", error);
      toast.error("Failed to save scope. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "" });
    setSelectedItems({
      rooms: new Set(),
      inclusions: new Set(),
      boqs: new Set(),
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getTotalSelectedCount = () => {
    return selectedItems.boqs.size;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          maxHeight: "90vh",
          overflowX: "clip",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontSize: "24px",
          fontWeight: 600,
          color: "#0D0D0E",
          paddingBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: 600, letterSpacing: 0 }}>
          {editingScope ? "Edit Scope" : "Create New Scope"}
        </div>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px" }}>
        {loadingData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Form Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <CInput
                label="Scope Name"
                placeholder="e.g. Kitchen Renovation Package"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={!!errors.name}
                helpertext={errors.name}
              />

              <CInput
                label="Description"
                multiline
                rows={4}
                placeholder="Brief description of this scope"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                error={!!errors.description}
                helpertext={errors.description}
              />
            </Box>

            {/* Items Selection */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  mb: 2,
                  letterSpacing: 0,
                }}
              >
                Select Items ({getTotalSelectedCount()} selected)
              </Typography>

              {errors.items && (
                <Typography color="error" sx={{ fontSize: "12px", mb: 2 }}>
                  {errors.items}
                </Typography>
              )}

              {roomBoqsData?.rooms?.map((room) => (
                <Box
                  key={room.room_id}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "16px",
                    backgroundColor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0,
                      mb: 2,
                    }}
                  >
                    <Checkbox
                      checked={selectedItems.rooms.has(room.room_id)}
                      onChange={() => handleRoomToggle(room.room_id)}
                      sx={{ padding: 0, transform: "scale(0.9)" }}
                    />
                    <Button
                      onClick={() => toggleRoomExpansion(room.room_id)}
                      sx={{
                        textTransform: "none",
                        color: "#0D0D0E",
                        fontWeight: 500,
                        padding: 0,
                        justifyContent: "flex-start",
                        flex: 1,
                        boxShadow: "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          flex: 1,
                        }}
                      >
                        <div style={{ flex: 1, textAlign: "left" }}>
                          🏠 {room.room_name}
                        </div>
                        <div style={{ flex: 1, textAlign: "right" }}>
                          (
                          {room.inclusions?.filter((inc) =>
                            selectedItems.inclusions.has(inc.id)
                          ).length || 0}
                          /{room.inclusions?.length || 0} items)
                        </div>
                      </div>
                    </Button>
                  </Box>

                  {expandedRooms.has(room.room_id) &&
                    room.inclusions?.map((inclusion) => (
                      <Box
                        key={inclusion.id}
                        sx={{
                          marginLeft: "24px",
                          marginBottom: "12px",
                          padding: "1vh 1vw",
                          border: "1px solid #f0f0f0",
                          borderRadius: "6px",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0,
                            mb: 1,
                          }}
                        >
                          <Checkbox
                            checked={selectedItems.inclusions.has(inclusion.id)}
                            onChange={() =>
                              handleInclusionToggle(inclusion.id, room.room_id)
                            }
                            sx={{ padding: 0, transform: "scale(0.9)" }}
                          />
                          <Button
                            onClick={() =>
                              toggleInclusionExpansion(inclusion.id)
                            }
                            sx={{
                              textTransform: "none",
                              color: "#0D0D0E",
                              fontWeight: 500,
                              padding: 0,
                              justifyContent: "flex-start",
                              flex: 1,
                              padding: "1vh 0",
                              boxShadow: "none",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                🔧 {inclusion.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                (
                                {inclusion.boqs?.filter((boq) =>
                                  selectedItems.boqs.has(boq.id)
                                ).length || 0}
                                /{inclusion.boqs?.length || 0})
                              </Typography>
                            </Box>
                          </Button>
                        </Box>

                        {expandedInclusions.has(inclusion.id) &&
                          inclusion.boqs?.map((boq) => (
                            <Box
                              key={boq.id}
                              sx={{
                                marginLeft: "24px",
                                padding: "8px 12px",
                                border: "1px solid #e8e8e8",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                marginBottom: "8px",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Checkbox
                                  checked={selectedItems.boqs.has(boq.id)}
                                  onChange={() =>
                                    handleBoqToggle(
                                      boq.id,
                                      inclusion.id,
                                      room.room_id
                                    )
                                  }
                                  sx={{ padding: 0, transform: "scale(0.9)" }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 0.5 }}
                                  >
                                    {boq.item_name}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        padding: "2px 6px",
                                        backgroundColor: "#e3f2fd",
                                        borderRadius: "4px",
                                        color: "#1976d2",
                                      }}
                                    >
                                      {boq.type}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        padding: "2px 6px",
                                        backgroundColor: "#f3e5f5",
                                        borderRadius: "4px",
                                        color: "#7b1fa2",
                                      }}
                                    >
                                      {boq.type === "Activities"
                                        ? `${boq.days || "-"} days`
                                        : `${boq.quantity || "-"} ${boq.unit_of_measurement || ""}`}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    ))}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "space-between" }}
      >
        <GreyStyledButton onClick={handleClose}>Cancel</GreyStyledButton>

        <StyledButton onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {editingScope ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>{editingScope ? "Update Scope" : "Add Scope"}</>
          )}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddScopeModal;
